import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

import { APP_API_URL, APP_NAME } from "../../utils";
import InvoiceComponent from "../../components/invoice";
import Loader from "../../components/loader";
import MainLayout from "../../layout/main-layout";
import formatNumber, { convertNumberToWordsInFCFA } from "../../utils/functions";

export default function BalanceInvoice() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;
  const [invoice, setInvoice] = useState({});
  const [isLoading, setLoading] = useState(false);

  // Fonction pour récupérer les données de la commande
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/customer-transactions/transactions/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setInvoice(response.data.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes :', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePrint = () => {
    const previousTitle = document.title;
    document.title = `ASEBât - ${invoice.designation} - Vente de matériaux de construction`;

    window.print();

    document.title = previousTitle;
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <MainLayout>
      <div className="mx-auto w-full max-w-6xl bg-white p-4 sm:p-6 lg:p-8">
        <div className="printable w-full">

          <div className="invoice-copy relative">
            {/* ================= HEADER ================= */}
            <div className="flex justify-between items-start border-b pb-4">
              <div>
                <img src="/assets/img/logo_v2.png" height={90} width={90} alt={APP_NAME} />

                <div className="mt-2 leading-5">
                  <p className="font-semibold">ASEBAT</p>
                  <p>C/1775 C, QUARTIER GBEDJEKOME - OUEDO</p>
                  <p>IFU : 3202434985932</p>
                  <p>info@asebat.com</p>
                  <p>+229 01 61 89 89 00</p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-xl font-bold uppercase">
                  {invoice?.type === 'debit' ? 'DÉCHARGE DE RETRAIT' : 'REÇU DE PAIEMENT'}
                </p>
                <p>Date : {new Date().toLocaleDateString("fr-FR")}</p>
              </div>
            </div>

            {/* ================= AGENT / MAGASIN ================= */}
            <div className="mt-4 grid grid-cols-2 gap-6">
              <div>
                <p>
                  <span className="font-bold">Agent</span> :{" "}
                  {user ? `${String(user.id).padStart(3, "0")} - ${user.name}` : "-"}
                </p>
                <p>
                  <span className="font-bold">Magasin</span> : {user?.stores?.[0]?.name || "-"}
                </p>
              </div>

              <div className="text-right">
                <p>
                  <span className="font-bold">Objet</span> :{" "}
                  {invoice?.type === 'debit'
                    ? 'Retrait du solde client'
                    : 'Versement sur le solde client'}
                </p>
              </div>
            </div>

            {/* ================= CLIENT ================= */}
            <div className="mt-5">
              <table className="w-full border border-black text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2">Code Client</th>
                    <th className="border p-2">Nom</th>
                    <th className="border p-2">IFU</th>
                    <th className="border p-2">Téléphone</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border p-2">{invoice?.customer?.client_code || "-"}</td>
                    <td className="border p-2">{invoice?.customer?.name || "-"}</td>
                    <td className="border p-2">{invoice?.customer?.ifu || "-"}</td>
                    <td className="border p-2">{invoice?.customer?.numero || "-"}</td>
                  </tr>
                  <tr>
                    <td colSpan={2} className='border'>{" "}</td>
                    <td className="border p-2 font-bold">
                      {invoice?.type === 'debit' ? 'Montant retiré' : 'Montant versé'}
                    </td>
                    <td className={`border p-2 fw-bold ${invoice?.type === 'debit' ? 'text-danger' : 'text-success'
                      }`}>
                      {formatNumber(invoice.amount)} FCFA
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="border"></td>
                    <td className="border p-2 font-bold">Ancien solde</td>
                    <td className="border p-2">
                      {formatNumber(invoice.previous_balance)} FCFA
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={2} className="border"></td>
                    <td className="border p-2 font-bold">Nouveau solde</td>
                    <td className="border p-2 font-bold">
                      {formatNumber(invoice.new_balance)} FCFA
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="border p-2">
                      <span className="font-bold">Montant en lettres : </span>
                      <span className="capitalize">
                        {convertNumberToWordsInFCFA(Math.round(invoice?.amount || 0))}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ================= LEGAL TEXT ================= */}
            <div className="mt-5 text-sm text-gray-700">
              {invoice?.type === 'debit'
                ? "Cette décharge atteste que le client a retiré le montant indiqué de son solde."
                : "Ce reçu atteste que le montant indiqué a été versé sur le solde du client."}
            </div>

            {/* ================= SIGNATURES ================= */}
            <div className="mt-10 flex justify-between">
              <div className="text-center">
                <p className="font-bold">Signature Client</p>
              </div>

              <div className="text-center">
                <p className="font-bold">Signature Agent</p>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div className="mt-40 text-center text-xs text-gray-600 border-t pt-2">
              ASEBAT © {new Date().getFullYear()} - Tous droits réservés
            </div>
          </div>

          <div className="modal-footer py-4 space-x-4 no-print">
            <button
              type="button"
              onClick={handlePrint} // Appel de la fonction handlePrint pour imprimer
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
            >
              Imprimer
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)} // Appel de la fonction handlePrint pour imprimer
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-300"
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}